import { Button } from '@trm/ui-kit';
import { cx } from '@trm/ui-kit/utils';
import type { ComponentPropsWithoutRef } from 'react';

import { AppleIcon } from './AppleIcon';
import { FacebookIcon } from './FacebookIcon';
import { GoogleIcon } from './GoogleIcon';

/**
 * Social login provider types supported by the authentication system.
 *
 * This type is used throughout the onboarding flow to specify which OAuth provider
 * the user wants to authenticate with. Each provider has specific branding requirements
 * and OAuth flow configurations that are handled internally by the component.
 *
 * **Supported Providers:**
 * - `google` - Google OAuth 2.0 sign-in
 * - `apple` - Sign in with Apple
 * - `facebook` - Facebook Login
 *
 * **Business Rules:**
 * - All providers follow their respective official branding guidelines
 * - Provider icons and colors are automatically applied based on the selected provider
 * - OAuth redirect URLs and scopes are configured per provider in the backend
 *
 * **Branding Guidelines:**
 * - Google: https://developers.google.com/identity/branding-guidelines
 * - Apple: https://developer.apple.com/design/human-interface-guidelines/sign-in-with-apple
 * - Facebook: https://developers.facebook.com/docs/facebook-login/web/login-button
 *
 * @example
 * ```tsx
 * // Using Google as the provider
 * const provider: SocialProvider = 'google';
 * <SocialLoginButton provider={provider} action="login" />
 * ```
 *
 * @example
 * ```tsx
 * // Dynamically selecting provider based on user choice
 * const [selectedProvider, setSelectedProvider] = useState<SocialProvider>('google');
 * <SocialLoginButton provider={selectedProvider} onClick={handleSocialLogin} />
 * ```
 */
export type SocialProvider = 'google' | 'apple' | 'facebook';

/**
 * Action type that determines the button text and semantic meaning.
 *
 * This type is used to contextualize what action the user is performing when they
 * click the social login button. The action directly affects the button's text label
 * and should match the context where the button is displayed (login page, signup page, etc.).
 *
 * **Available Actions:**
 * - `login` - For existing users signing in (displays "Login with [Provider]")
 * - `signup` - For new users creating an account (displays "Sign up with [Provider]")
 * - `continue` - For general authentication flows (displays "Continue with [Provider]")
 *
 * **Default Behavior:**
 * - If not specified, defaults to `'login'`
 * - The actual OAuth flow is the same regardless of action; this only affects the UI text
 *
 * **Usage Guidelines:**
 * - Use `'login'` on dedicated login pages or when returning users are expected
 * - Use `'signup'` on registration pages or during new user onboarding
 * - Use `'continue'` for neutral contexts or multi-step flows where the user's intent is ambiguous
 *
 * @example
 * ```tsx
 * // Login page context
 * <SocialLoginButton provider="google" action="login" />
 * // Button displays: "Login with Google"
 * ```
 *
 * @example
 * ```tsx
 * // Signup/registration page context
 * <SocialLoginButton provider="apple" action="signup" />
 * // Button displays: "Sign up with Apple"
 * ```
 *
 * @example
 * ```tsx
 * // Neutral onboarding flow
 * <SocialLoginButton provider="facebook" action="continue" />
 * // Button displays: "Continue with Facebook"
 * ```
 */
export type SocialAction = 'login' | 'signup' | 'continue';

/**
 * Configuration for each social provider
 * Following official branding guidelines:
 * - Google: https://developers.google.com/identity/branding-guidelines
 * - Apple: https://developer.apple.com/design/human-interface-guidelines/sign-in-with-apple
 * - Facebook: https://www.meta.com/brand/resources/facebook/logo/
 */
const providerConfig = {
  google: {
    icon: GoogleIcon,
    name: 'Google',
  },
  apple: {
    icon: AppleIcon,
    name: 'Apple',
  },
  facebook: {
    icon: FacebookIcon,
    name: 'Facebook',
  },
};

/**
 * Get the button text based on provider and action
 */
function getButtonText(provider: SocialProvider, action: SocialAction): string {
  const providerName = providerConfig[provider].name;

  switch (action) {
    case 'login':
      return `Login with ${providerName}`;
    case 'signup':
      return `Sign up with ${providerName}`;
    case 'continue':
      return `Continue with ${providerName}`;
    default:
      return `Continue with ${providerName}`;
  }
}

/**
 * Props for the SocialLoginButton component.
 *
 * This interface defines all configurable properties for the social login button,
 * extending native HTML button props while enforcing provider-specific behavior.
 * The component is used throughout the authentication flow (login, signup, onboarding)
 * to provide a consistent OAuth authentication experience.
 *
 * **Key Features:**
 * - Extends native button props for full HTML button API support
 * - Enforces provider-specific branding automatically
 * - Manages loading states during OAuth redirects
 * - Provides keyboard accessibility (Enter/Space)
 * - Includes proper ARIA labels for screen readers
 *
 * **Business Constraints:**
 * - `provider` is required and must be one of the supported providers
 * - `action` defaults to `'login'` if not specified
 * - `isLoading` prevents interaction and shows loading indicator
 * - Button uses `aria-disabled` instead of native `disabled` for better accessibility
 * - All provider icons follow their official branding guidelines
 *
 * **Accessibility Considerations:**
 * - Button has proper aria-label matching the button text
 * - Loading state sets aria-disabled to prevent interaction
 * - Icons are marked aria-hidden to avoid duplication
 * - Keyboard navigation works with Enter and Space keys
 *
 * **OAuth Flow:**
 * - Click handler should initiate OAuth redirect to provider
 * - Loading state should be set true immediately on click
 * - Backend handles actual OAuth authentication
 * - Success/failure handled after redirect callback
 *
 * @example
 * ```tsx
 * // Basic usage with Google login
 * <SocialLoginButton
 *   provider="google"
 *   action="login"
 *   onClick={() => window.location.href = '/auth/google'}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With loading state during OAuth flow
 * const [isLoading, setIsLoading] = useState(false);
 *
 * const handleGoogleLogin = () => {
 *   setIsLoading(true);
 *   window.location.href = '/auth/google'; // Redirects to OAuth provider
 * };
 *
 * <SocialLoginButton
 *   provider="google"
 *   action="login"
 *   isLoading={isLoading}
 *   onClick={handleGoogleLogin}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Signup context with custom styling
 * <SocialLoginButton
 *   provider="apple"
 *   action="signup"
 *   className="w-full mt-4"
 *   onClick={handleAppleSignup}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // All three providers in an authentication page
 * const handleSocialAuth = (provider: SocialProvider) => {
 *   initiateOAuth(provider);
 * };
 *
 * <div className="flex flex-col gap-3">
 *   <SocialLoginButton
 *     provider="google"
 *     action="continue"
 *     onClick={() => handleSocialAuth('google')}
 *   />
 *   <SocialLoginButton
 *     provider="apple"
 *     action="continue"
 *     onClick={() => handleSocialAuth('apple')}
 *   />
 *   <SocialLoginButton
 *     provider="facebook"
 *     action="continue"
 *     onClick={() => handleSocialAuth('facebook')}
 *   />
 * </div>
 * ```
 *
 * @see {@link SocialProvider} for available OAuth providers
 * @see {@link SocialAction} for action type options
 */
export interface SocialLoginButtonProps extends Omit<
  ComponentPropsWithoutRef<'button'>,
  'children'
> {
  /**
   * OAuth provider to use for authentication.
   *
   * Each provider has specific branding requirements that are automatically applied.
   * The provider determines which OAuth flow to initiate when the button is clicked.
   *
   * @required
   * @see {@link SocialProvider} for available options and branding guidelines
   */
  provider: SocialProvider;

  /**
   * Action type that determines the button text.
   *
   * Use `'login'` for returning users, `'signup'` for new user registration,
   * or `'continue'` for neutral contexts. This only affects the button text;
   * the OAuth flow remains the same regardless of action.
   *
   * @default 'login'
   * @see {@link SocialAction} for available options and usage guidelines
   */
  action?: SocialAction;

  /**
   * Whether the button is in a loading state.
   *
   * When true, the button shows a loading spinner and becomes disabled to prevent
   * duplicate submissions. Set this to true immediately when initiating the OAuth flow,
   * as the redirect may take a moment to complete.
   *
   * @default false
   *
   * @example
   * ```tsx
   * const [loading, setLoading] = useState(false);
   * const handleLogin = () => {
   *   setLoading(true);
   *   initiateOAuth('google');
   * };
   * ```
   */
  isLoading?: boolean;

  /**
   * Additional CSS class names to apply to the button.
   *
   * Use this to customize sizing, spacing, or other styling while maintaining
   * the provider-specific colors and branding.
   *
   * @example
   * ```tsx
   * <SocialLoginButton
   *   provider="google"
   *   className="w-full mt-4"
   * />
   * ```
   */
  className?: string;
}

/**
 * Social Login Button component with provider-specific styling
 *
 * Follows official branding guidelines for Google, Apple, and Facebook.
 * Features:
 * - Provider-specific colors and icons
 * - Loading states during redirect
 * - Keyboard accessible (Enter/Space triggers)
 * - Screen reader friendly with proper ARIA labels
 * - Mobile responsive
 *
 * @example
 * ```tsx
 * <SocialLoginButton
 *   provider="google"
 *   action="login"
 *   onClick={handleGoogleLogin}
 * />
 * ```
 *
 * @see {@link SocialProvider} for available providers
 * @see {@link SocialAction} for action types
 */
export function SocialLoginButton({
  provider,
  action = 'login',
  isLoading = false,
  className,
  disabled,
  onClick,
  ...rest
}: SocialLoginButtonProps) {
  const config = providerConfig[provider];
  const Icon = config.icon;
  const buttonText = getButtonText(provider, action);

  return (
    <Button
      {...rest}
      color="secondary"
      isDisabled={disabled}
      isLoading={isLoading}
      onClick={onClick}
      iconLeading={<Icon className="mr-1" />}
      size="lg"
      className={cx('w-full bg-gray-950 font-normal ring-1 ring-disabled', className)}
      aria-label={buttonText}
    >
      {buttonText}
    </Button>
  );
}
